/**
 * Reisegruppen-Widget T5 — Anzeige der Angaben einer Widget-Anfrage im Vorgang.
 *
 * Rein (kein React): Anfragenummer und die Zeilen der Karte „Angaben aus der Anfrage“. Die Nummer
 * ist eine Kurzkennung aus der Anfrage-ID (`A-` + die ersten sechs Hex-Zeichen, gross), dieselbe
 * Regel wie `anfrageKennung` im Server (lib/public-inquiry.ts); der Stamm-Vorgang hat keine
 * eigene `events.number`. Sie ist kein Geheimnis und kein Zugriffsschluessel.
 */
import type { AnfrageAngaben } from "../ui/types";

export function anfrageNummer(inquiryId: string): string {
  return `A-${inquiryId.replace(/[^0-9a-fA-F]/g, "").slice(0, 6).toUpperCase()}`;
}

/** „2026-12-11“ → „Fr., 11.12.2026“ (Wochentag kurz, wie im Kalender der Widgets). Unlesbar → der Rohwert. */
export function tagAnzeige(tag: string): string {
  const m = /^(\d{4})-(0[1-9]|1[0-2])-(\d{2})$/.exec(tag);
  if (!m) return tag;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
}

/** „2027-05“ → „Mai 2027“ (immer vierstelliges Jahr). Unlesbar → der Rohwert. */
export function monatAnzeige(monat: string): string {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(monat);
  if (!m) return monat;
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

const NACHTRAG_LABEL: Record<string, string> = {
  mahlzeit: "Mittag- oder Abendessen",
  ankunft: "Ankunft (ungefähr)",
  mobil: "Handynummer für den Reisetag",
  firma: "Firma / Organisation",
  rolle: "Fragt an als",
  ernaehrung: "Besondere Ernährung",
  ernaehrungDetails: "Ernährung, Details",
  reiseplan: "Reiseplan",
  nachricht: "Nachricht",
};
const WERT_LABEL: Record<string, string> = {
  mittag: "Mittagessen",
  abend: "Abendessen",
  offen: "noch offen",
  reiseveranstalter: "Reiseveranstalter",
  reiseleitung: "Reiseleitung",
  firma: "Firma",
  privat: "Privat",
  ja: "Ja",
  nein: "Nein",
};
/** Reihenfolge der nachgereichten Angaben (wie auf der Dankeseite). */
const NACHTRAG_REIHE = ["mahlzeit", "ankunft", "mobil", "firma", "rolle", "ernaehrung", "ernaehrungDetails", "reiseplan", "nachricht"];

export interface AngabeZeile {
  key: string;
  label: string;
  wert: string;
  /** Vom Gast erst NACH dem Absenden nachgereicht. */
  nachgereicht: boolean;
}

/** Zeilen der Karte; leer, wenn die Anfrage nichts davon traegt. */
export function angabenZeilen(a: AnfrageAngaben | null | undefined): AngabeZeile[] {
  if (!a) return [];
  const zeilen: AngabeZeile[] = [];
  // E2a: mit Kalender steht ein konkreter Tag da (und optional ein Ausweichtermin); der Monat waere doppelt.
  if (a.wunschdatum) {
    zeilen.push({ key: "wunschdatum", label: "Wunschtag", wert: tagAnzeige(a.wunschdatum), nachgereicht: false });
    if (a.ausweichdatum) zeilen.push({ key: "ausweichdatum", label: "Ausweichtermin", wert: tagAnzeige(a.ausweichdatum), nachgereicht: false });
  } else if (a.terminOffen) zeilen.push({ key: "terminOffen", label: "Wunschzeitraum", wert: "Termin noch offen", nachgereicht: false });
  else if (a.wunschMonat) zeilen.push({ key: "wunschMonat", label: "Wunschzeitraum", wert: monatAnzeige(a.wunschMonat), nachgereicht: false });
  if (a.gaesteBereich) zeilen.push({ key: "gaesteBereich", label: "Gruppengröße", wert: `${a.gaesteBereich} Personen`, nachgereicht: false });
  const n = a.nachtrag ?? {};
  for (const key of NACHTRAG_REIHE) {
    const roh = n[key];
    if (typeof roh !== "string" || !roh.trim()) continue;
    zeilen.push({ key: `nachtrag.${key}`, label: NACHTRAG_LABEL[key] ?? key, wert: WERT_LABEL[roh] ?? roh, nachgereicht: true });
  }
  return zeilen;
}

/* ---------------------------------------------------------------------------------------------
 * Punkt 130 (Antoine, 30.09.2026): „Wenn eine Reisegruppe etc. nur einen Monat angibt, muss der
 * Monat im Datumsfeld stehen … als Abkürzung: Dez, Jan etc.“ Eine Stelle für alle Anfragewege:
 * das Reisegruppen-Widget legt `details.wunschMonat` ab (maßgeblich); Formular, Chat und Mail
 * kennen kein Monatsfeld — dort wird der Monat aus dem Anfragetext gelesen.
 * ------------------------------------------------------------------------------------------- */

/** Monatsabkürzungen, wie Antoine sie nennt (nicht Intl: das liefert „Sept.“, „Juni“ …). */
export const MONAT_KURZ = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"] as const;

// Englisch „May“ fehlt bewusst: zu oft das Hilfsverb („we may come“).
const MONATSNUMMER: Record<string, number> = {
  januar: 1, jänner: 1, january: 1, februar: 2, february: 2, märz: 3, maerz: 3, march: 3, april: 4, mai: 5,
  juni: 6, june: 6, juli: 7, july: 7, august: 8, september: 9, oktober: 10, october: 10, november: 11,
  dezember: 12, december: 12,
};
const MONAT_IM_TEXT =
  /(?<!\p{L})(januar|jänner|january|februar|february|märz|maerz|march|april|mai|juni|june|juli|july|august|september|oktober|october|november|dezember|december)(?:\s+(20\d\d))?(?!\p{L})/giu;

/**
 * Genau EIN genannter Monat im Text → `YYYY-MM`; kein oder mehrere verschiedene Monate → `null`
 * (nicht raten). Jahr: ausdrücklich dahinter („Dezember 2027“), sonst das nächste Vorkommen ab
 * dem Eingang (`bezug`, `YYYY-MM…`) — derselbe Monat zählt als „dieses Jahr“.
 * ponytail: reiner Wortabgleich — „letzten Dezember waren wir schon da“ zählt auch; genauer wäre
 * ein Monatsfeld im KI-Import (`lib/inquiry-parse.ts`), wenn solche Fehlgriffe auftauchen.
 */
export function monatAusText(text: string | null | undefined, bezug: string | null | undefined): string | null {
  if (!text) return null;
  const b = /^(\d{4})-(\d{2})/.exec(bezug ?? "");
  let monat: number | null = null;
  let jahr: number | null = null;
  for (const m of text.matchAll(MONAT_IM_TEXT)) {
    const nr = MONATSNUMMER[(m[1] ?? "").toLowerCase()];
    if (nr === undefined) continue;
    if (monat !== null && monat !== nr) return null;
    monat = nr;
    if (m[2] && jahr === null) jahr = Number(m[2]);
  }
  if (monat === null) return null;
  if (jahr === null) {
    if (!b) return null;
    jahr = Number(b[1]) + (monat < Number(b[2]) ? 1 : 0);
  }
  return `${jahr}-${String(monat).padStart(2, "0")}`;
}

/**
 * Der Wunschmonat eines Vorgangs, solange KEIN Tag feststeht (`YYYY-MM`), sonst `null`.
 * Vorrang: genauer Tag (dann nichts) → „Termin noch offen“ (nichts) → Widget-Monat → Anfragetext.
 */
export function wunschMonatVon(q: {
  eventDate?: string | null;
  angaben?: Pick<AnfrageAngaben, "wunschMonat" | "terminOffen"> | null;
  text?: string | null;
  eingangAm?: string | null;
}): string | null {
  if (q.eventDate) return null;
  if (q.angaben?.terminOffen) return null;
  if (q.angaben?.wunschMonat && /^\d{4}-(0[1-9]|1[0-2])$/.test(q.angaben.wunschMonat)) return q.angaben.wunschMonat;
  return monatAusText(q.text, q.eingangAm);
}

/** „2026-12“ → `{ kurz: "Dez", jahr: "2026" }`; unlesbar → `null`. */
export function monatTeile(monat: string | null | undefined): { kurz: string; jahr: string } | null {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(monat ?? "");
  return m ? { kurz: MONAT_KURZ[Number(m[2]) - 1]!, jahr: m[1]! } : null;
}

/** „Dez“ im laufenden Jahr, sonst „Jan 2027“ — für Textstellen (Checkliste, Kanban). */
export function monatKurzText(monat: string | null | undefined, heute: Date = new Date()): string | null {
  const t = monatTeile(monat);
  if (!t) return null;
  return Number(t.jahr) === heute.getFullYear() ? t.kurz : `${t.kurz} ${t.jahr}`;
}

/** Listen/Kanban: „Dez“ / „Jan 2027“ für eine Anfrage ohne Tag, sonst `null`. */
export function listenMonat(
  i: { eventDate: string | null; wunschMonat?: string | null; terminOffen?: boolean; message?: string | null; createdAt?: string | null },
  heute: Date = new Date(),
): string | null {
  const monat = wunschMonatVon({
    eventDate: i.eventDate,
    angaben: { wunschMonat: i.wunschMonat ?? undefined, terminOffen: i.terminOffen },
    text: i.message,
    eingangAm: i.createdAt,
  });
  return monatKurzText(monat, heute);
}
